import { useEffect, useRef, useState } from "react";
import { Settings2, Lock, Unlock, Eye, EyeOff, Copy, Trash2 } from "lucide-react";
import type { Chart as KChart } from "klinecharts";

import { useDrawingStore } from "@/stores/drawingStore";
import { useUIStore } from "@/stores/uiStore";
import {
  setDrawingRightClickHandler,
  setDrawingLocked,
  setDrawingVisible,
  duplicateDrawing,
  removeDrawing,
} from "@/lib/lytick/overlay-bridge";

interface DrawingContextMenuProps {
  containerRef: React.RefObject<HTMLDivElement | null>;
  chartRef: React.RefObject<KChart | null>;
}

interface MenuState {
  x: number;
  y: number;
  drawingId: string;
}

// ─── pixel hit testing ────────────────────────────────────────────────────────

function pointToSegmentDist(
  px: number,
  py: number,
  x1: number,
  y1: number,
  x2: number,
  y2: number,
): number {
  const dx = x2 - x1;
  const dy = y2 - y1;
  const lenSq = dx * dx + dy * dy;
  if (lenSq === 0) return Math.hypot(px - x1, py - y1);
  const t = Math.max(0, Math.min(1, ((px - x1) * dx + (py - y1) * dy) / lenSq));
  return Math.hypot(px - (x1 + t * dx), py - (y1 + t * dy));
}

function findDrawingAtPixel(
  px: number,
  py: number,
  chart: KChart,
  drawingIds: Set<string>,
): string | null {
  const overlays = chart.getOverlays();
  const HIT_RADIUS = 24;
  let bestId: string | null = null;
  let bestDist = HIT_RADIUS;

  for (const overlay of overlays) {
    if (!drawingIds.has(overlay.id)) continue;
    const points = overlay.points;
    if (!points || points.length === 0) continue;

    try {
      if (points.length === 1) {
        // Single-point overlays (horizontal lines): only vertical distance matters
        const pixelResult = chart.convertToPixel(points[0]);
        const pixelPt = Array.isArray(pixelResult) ? pixelResult[0] : pixelResult;
        const d = Math.abs((pixelPt?.y ?? Infinity) - py);
        if (d < bestDist) {
          bestDist = d;
          bestId = overlay.id;
        }
      } else {
        // Multi-point overlays: check distance to each line segment
        const pixelResult = chart.convertToPixel(points);
        const pixelPts = Array.isArray(pixelResult) ? pixelResult : [pixelResult];
        for (let i = 0; i < pixelPts.length - 1; i++) {
          const p1 = pixelPts[i];
          const p2 = pixelPts[i + 1];
          if (p1?.x == null || p1?.y == null || p2?.x == null || p2?.y == null) continue;
          const d = pointToSegmentDist(px, py, p1.x, p1.y, p2.x, p2.y);
          if (d < bestDist) {
            bestDist = d;
            bestId = overlay.id;
          }
        }
      }
    } catch {
      // Ignore coordinate conversion errors on unmounted charts
    }
  }
  return bestId;
}

// ─── component ────────────────────────────────────────────────────────────────

export function DrawingContextMenu({ containerRef, chartRef }: DrawingContextMenuProps) {
  const [menu, setMenu] = useState<MenuState | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const longPressTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const { drawings, setSelectedDrawingIds } = useDrawingStore();
  const { openDrawer } = useUIStore();

  // Mobile: long-press detection
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const handleTouchStart = (e: TouchEvent) => {
      if (e.touches.length !== 1) return;
      const touch = e.touches[0];
      const touchClientX = touch.clientX;
      const touchClientY = touch.clientY;

      longPressTimerRef.current = setTimeout(() => {
        const chart = chartRef.current;
        if (!chart) return;

        const rect = container.getBoundingClientRect();
        const px = touchClientX - rect.left;
        const py = touchClientY - rect.top;

        // Use current store state (not stale closure)
        const currentDrawings = useDrawingStore.getState().drawings;
        const drawingIds = new Set(currentDrawings.map((d) => d.id));
        const drawingId = findDrawingAtPixel(px, py, chart, drawingIds);

        if (drawingId) {
          setSelectedDrawingIds([drawingId]);
          setMenu({ x: touchClientX, y: touchClientY, drawingId });
        }
      }, 500);
    };

    const cancelTimer = () => {
      if (longPressTimerRef.current) {
        clearTimeout(longPressTimerRef.current);
        longPressTimerRef.current = null;
      }
    };

    container.addEventListener("touchstart", handleTouchStart, { passive: true });
    container.addEventListener("touchend", cancelTimer);
    container.addEventListener("touchmove", cancelTimer, { passive: true });

    return () => {
      container.removeEventListener("touchstart", handleTouchStart);
      container.removeEventListener("touchend", cancelTimer);
      container.removeEventListener("touchmove", cancelTimer);
      cancelTimer();
    };
  }, [containerRef, chartRef, setSelectedDrawingIds]);

  // Desktop: bridge right-click callback
  useEffect(() => {
    setDrawingRightClickHandler((drawingId, x, y) => {
      setMenu({ x, y, drawingId });
    });
    return () => setDrawingRightClickHandler(null);
  }, []);

  // Dismiss on outside click/touch
  useEffect(() => {
    if (!menu) return;
    const dismiss = (e: MouseEvent | TouchEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenu(null);
      }
    };
    document.addEventListener("mousedown", dismiss);
    document.addEventListener("touchstart", dismiss, { passive: true });
    return () => {
      document.removeEventListener("mousedown", dismiss);
      document.removeEventListener("touchstart", dismiss);
    };
  }, [menu]);

  if (!menu) return null;

  const drawing = drawings.find((d) => d.id === menu.drawingId);
  if (!drawing) return null;

  const act = (fn: () => void) => {
    fn();
    setMenu(null);
  };

  return (
    <div
      ref={menuRef}
      style={{ position: "fixed", left: menu.x, top: menu.y }}
      className="z-[9999] min-w-[160px] overflow-hidden rounded-xl border border-border bg-card shadow-xl"
    >
      <button
        type="button"
        className="flex h-10 w-full items-center gap-2.5 px-4 text-sm hover:bg-surface transition-colors"
        onClick={() =>
          act(() => {
            setSelectedDrawingIds([menu.drawingId]);
            openDrawer("tools");
          })
        }
      >
        <Settings2 className="size-4 shrink-0" />
        Edit
      </button>

      <button
        type="button"
        className="flex h-10 w-full items-center gap-2.5 px-4 text-sm hover:bg-surface transition-colors"
        onClick={() => act(() => setDrawingLocked(menu.drawingId, !drawing.locked))}
      >
        {drawing.locked ? (
          <Unlock className="size-4 shrink-0" />
        ) : (
          <Lock className="size-4 shrink-0" />
        )}
        {drawing.locked ? "Unlock" : "Lock"}
      </button>

      <button
        type="button"
        className="flex h-10 w-full items-center gap-2.5 px-4 text-sm hover:bg-surface transition-colors"
        onClick={() => act(() => setDrawingVisible(menu.drawingId, !drawing.visible))}
      >
        {drawing.visible ? (
          <EyeOff className="size-4 shrink-0" />
        ) : (
          <Eye className="size-4 shrink-0" />
        )}
        {drawing.visible ? "Hide" : "Show"}
      </button>

      <button
        type="button"
        className="flex h-10 w-full items-center gap-2.5 px-4 text-sm hover:bg-surface transition-colors"
        onClick={() => act(() => duplicateDrawing(menu.drawingId))}
      >
        <Copy className="size-4 shrink-0" />
        Duplicate
      </button>

      <div className="border-t border-border" />

      <button
        type="button"
        className="flex h-10 w-full items-center gap-2.5 px-4 text-sm text-loss hover:bg-surface transition-colors"
        onClick={() => act(() => removeDrawing(menu.drawingId))}
      >
        <Trash2 className="size-4 shrink-0" />
        Delete
      </button>
    </div>
  );
}
